import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { formatUsd, formatDate, shortId } from '../lib/format';
import { OrderExternalLinks } from '../components/ExternalLinks';
import { HoldBadges, holdState } from '../components/OrderHold';
import { TestBadge } from '../components/TestBadge';
import { DisputeBadge } from '../components/DisputeBadge';

interface Order {
  uuid: string;
  total: number;
  payment_status: string;
  status: string;
  fulfillment_status: string;
  customer_email: string;
  customer_name: string;
  created_at: string;
  gelato_dispatch_pending: number;
  is_test: number;
  dispute_status: string | null;
  gelato_order_id: string | null;
  stripe_checkout_session_id: string | null;
  stripe_payment_intent_id: string | null;
  review_required: number;
  approved_at: string | null;
  funds_required: number;
  funds_status: string | null;
  released_early_by: string | null;
}

export function OrdersList() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    // "held" is a hold filter, not an order status.
    if (status === 'held') params.set('held', '1');
    else if (status) params.set('status', status);
    params.set('limit', '100');
    api
      .get<{ orders: Order[] }>(`/api/admin/orders?${params}`)
      .then((d) => setOrders(d.orders))
      .catch(console.error);
  }, [query, status]);

  return (
    <div>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400 }}>Orders</h1>
      <div style={{ display: 'flex', gap: 12, margin: '16px 0 24px' }}>
        <input
          placeholder="Search email or order id"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={inputStyle}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle}>
          <option value="">All statuses</option>
          <option value="held">Held from Gelato</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ ...rowStyle, fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>
          <span>Order</span>
          <span>Customer</span>
          <span>Status</span>
          <span>Placed</span>
          <span>Open in</span>
          <span style={{ textAlign: 'right' }}>Total</span>
        </div>
        {orders.length === 0 && (
          <p style={{ padding: 24, color: '#6b7280', margin: 0 }}>No orders found.</p>
        )}
        {/* Rows navigate on click instead of being links, so the Stripe/Gelato
            buttons inside aren't nested anchors. */}
        {orders.map((o) => (
          <div
            key={o.uuid}
            role="link"
            tabIndex={0}
            onClick={() => navigate(`/orders/${o.uuid}`)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`/orders/${o.uuid}`)}
            style={{ ...rowStyle, cursor: 'pointer' }}
          >
            <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
              #{shortId(o.uuid)}
              {o.is_test === 1 ? (
                <TestBadge />
              ) : (
                // Held orders haven't failed — they're waiting; see the status badges.
                o.gelato_dispatch_pending === 1 && !holdState(o).held && (
                  <span style={{ color: '#b91c1c', marginLeft: 6 }}>!</span>
                )
              )}
              {o.dispute_status && <DisputeBadge status={o.dispute_status} />}
            </span>
            <span style={{ fontSize: 14 }}>{o.customer_email}</span>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              {o.status}
              <HoldBadges order={o} />
            </span>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{formatDate(o.created_at)}</span>
            <span>
              <OrderExternalLinks order={o} compact />
            </span>
            <span style={{ fontSize: 14, textAlign: 'right' }}>{formatUsd(o.total)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '120px 1fr 110px 160px 150px 100px',
  gap: 16,
  padding: '14px 20px',
  borderBottom: '1px solid #f0f0f0',
  alignItems: 'center',
  textDecoration: 'none',
  color: 'inherit',
};

const inputStyle: React.CSSProperties = {
  padding: '8px 12px',
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  fontSize: 14,
  fontFamily: 'inherit',
  flex: 1,
};
